"use client";

import React, { useState } from "react";

interface LinkedAccount {
	type: string;
	address?: string;
	walletAddress?: string;
	number?: string;
	email?: string;
	username?: string;
	name?: string;
	walletClientType?: string;
}

interface ConnectedAccountsSectionProps {
	linkedAccounts: LinkedAccount[];
	isMobile: boolean;
	hasPhantom: boolean;
	isSyncing: boolean;
	linkEmail: () => void;
	linkEmailWithPreCheck?: (
		email: string
	) => Promise<"duplicate" | "ok" | "error">;
	linkPhone: () => void;
	linkPhoneWithPreCheck?: (
		phone: string
	) => Promise<"duplicate" | "ok" | "error">;
	linkTwitter: () => void;
	linkGoogle: () => void;
	linkDiscord: () => void;
	linkPhantom: () => void;
	unlinkEmail: (email: string) => Promise<void>;
	unlinkPhone: (phone: string) => Promise<void>;
	unlinkTwitter: (subject: string) => Promise<void>;
	unlinkGoogle: (subject: string) => Promise<void>;
	unlinkDiscord: (subject: string) => Promise<void>;
	unlinkPhantom: (address: string) => Promise<void>;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Loose phone validation: at least 7 digits, optional leading +, allow spaces,
// dashes and parentheses. Privy enforces strict E.164 in its own modal.
const PHONE_REGEX = /^\+?[\d\s().\-]{7,}$/;

const ACCOUNT_ICONS: Record<string, React.ReactNode> = {
	email: (
		<svg
			className="w-4 h-4"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
		>
			<path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
		</svg>
	),
	phone: (
		<svg
			className="w-4 h-4"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
		>
			<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.7A2 2 0 012.18 1H5.1a2 2 0 012 1.72 12.6 12.6 0 00.7 2.81 2 2 0 01-.45 2.11L6.11 8.8a16 16 0 006.72 6.72l1.16-1.16a2 2 0 012.11-.45 12.6 12.6 0 002.81.7A2 2 0 0122 16.92z" />
		</svg>
	),
	wallet: (
		<svg
			className="w-4 h-4"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
		>
			<path d="M21 12V7H5a2 2 0 010-4h14v4M21 12v5H8a2 2 0 000 4h13v-4" />
		</svg>
	),
	twitter_oauth: (
		<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
			<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
		</svg>
	),
	google_oauth: (
		<svg className="w-4 h-4" viewBox="0 0 24 24">
			<path
				fill="#4285F4"
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
			/>
			<path
				fill="#34A853"
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
			/>
			<path
				fill="#FBBC05"
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
			/>
			<path
				fill="#EA4335"
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
			/>
		</svg>
	),
	discord_oauth: (
		<svg className="w-4 h-4" viewBox="0 0 24 24" fill="#5865F2">
			<path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.04.036.05a19.863 19.863 0 0 0 5.993 3.03.077.077 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
		</svg>
	),
};

function accountLabel(account: LinkedAccount): string {
	switch (account.type) {
		case "email":
			return account.address ?? account.email ?? "Email";
		case "phone":
			return account.number ?? "Phone";
		case "wallet": {
			const addr = account.address ?? account.walletAddress ?? "";
			const wct = (account as any).walletClientType ?? "";
			const label = wct ? wct.charAt(0).toUpperCase() + wct.slice(1) : "Wallet";
			return addr ? `${label} (${addr.slice(0, 4)}…${addr.slice(-4)})` : label;
		}
		case "twitter_oauth":
			return `@${account.username ?? "Twitter"}`;
		case "google_oauth":
			return account.email ?? account.name ?? "Google";
		case "discord_oauth":
			return account.username ?? "Discord";
		default:
			return account.type;
	}
}

function accountColor(type: string): string {
	switch (type) {
		case "twitter_oauth":
			return "text-sky-400";
		case "google_oauth":
			return "text-blue-400";
		case "discord_oauth":
			return "text-indigo-400";
		case "wallet":
			return "text-[#E8C5A0]";
		default:
			return "text-gray-300";
	}
}

export const ConnectedAccountsSection: React.FC<
	ConnectedAccountsSectionProps
> = ({
	linkedAccounts,
	isMobile,
	hasPhantom,
	isSyncing,
	linkEmail,
	linkEmailWithPreCheck,
	linkPhone,
	linkPhoneWithPreCheck,
	linkTwitter,
	linkGoogle,
	linkDiscord,
	linkPhantom,
	unlinkEmail,
	unlinkPhone,
	unlinkTwitter,
	unlinkGoogle,
	unlinkDiscord,
	unlinkPhantom,
}) => {
	// Pre-check email entry state. Used to surface a duplicate-account merge
	// prompt before opening Privy's link modal (Task 8). When
	// `linkEmailWithPreCheck` is not provided we fall back to the legacy
	// direct-link button.
	const [showEmailInput, setShowEmailInput] = useState(false);
	const [emailValue, setEmailValue] = useState("");
	const [emailLinkState, setEmailLinkState] = useState<
		"idle" | "checking" | "error"
	>("idle");
	const [emailFieldError, setEmailFieldError] = useState<string | null>(null);

	// Phone pre-check state — same pattern as email (Task 10).
	const [showPhoneInput, setShowPhoneInput] = useState(false);
	const [phoneValue, setPhoneValue] = useState("");
	const [phoneLinkState, setPhoneLinkState] = useState<
		"idle" | "checking" | "error"
	>("idle");
	const [phoneFieldError, setPhoneFieldError] = useState<string | null>(null);
	const hasType = (type: string) => linkedAccounts.some((a) => a.type === type);
	const hasExternalWallet = linkedAccounts.some(
		(a) =>
			a.type === "wallet" &&
			(a as any).walletClientType !== "privy" &&
			(a as any).walletClientType !== "smart_wallet",
	);

	const getUnlinkHandler = (account: LinkedAccount) => {
		switch (account.type) {
			case "email":
				return () => unlinkEmail(account.address ?? account.email ?? "");
			case "phone":
				return () => unlinkPhone(account.number ?? "");
			case "twitter_oauth":
				return () =>
					unlinkTwitter((account as any).subject ?? account.username ?? "");
			case "google_oauth":
				return () =>
					unlinkGoogle((account as any).subject ?? account.email ?? "");
			case "discord_oauth":
				return () =>
					unlinkDiscord((account as any).subject ?? account.username ?? "");
			case "wallet": {
				const addr = account.address ?? account.walletAddress ?? "";
				const wct = (account as any).walletClientType;
				if (wct === "phantom" || wct === "solflare") {
					return () => unlinkPhantom(addr);
				}
				return null;
			}
			default:
				return null;
		}
	};

	return (
		<div className="bg-[#1A1A1A] rounded-xl p-6 space-y-4">
			<h2 className="text-lg font-semibold text-mgt-green">
				Connected Accounts
			</h2>

			{/* Linked accounts list */}
			<div className="space-y-2">
				{linkedAccounts.map((account, i) => {
					const unlinkHandler = getUnlinkHandler(account);
					const canUnlink = linkedAccounts.length > 1 && !!unlinkHandler;
					return (
						<div
							key={`${account.type}-${i}`}
							className="flex items-center justify-between bg-[#231F20] rounded-lg px-3 py-2.5"
						>
							<div
								className={`flex items-center gap-2 ${accountColor(account.type)}`}
							>
								{ACCOUNT_ICONS[account.type] ?? ACCOUNT_ICONS.wallet}
								<span className="text-sm truncate max-w-[220px]">
									{accountLabel(account)}
								</span>
							</div>
							{canUnlink && (
								<button
									onClick={unlinkHandler}
									disabled={isSyncing}
									className="text-xs text-red-400 hover:text-red-300 disabled:opacity-40 transition-colors"
								>
									Unlink
								</button>
							)}
						</div>
					);
				})}
			</div>

			{/* Email pre-check mini-form — surfaces duplicate accounts before
			    opening Privy's modal (Task 8). */}
			{!hasType("email") && linkEmailWithPreCheck && showEmailInput && (
				<div className="bg-[#231F20] border border-white/10 rounded-lg p-3 space-y-2">
					<p className="text-xs text-gray-300">
						Enter the email you want to link. We&apos;ll check if it already
						belongs to another account before sending a code.
					</p>
					<div className="flex gap-2">
						<input
							type="email"
							autoFocus
							value={emailValue}
							onChange={(e) => {
								setEmailValue(e.target.value);
								setEmailFieldError(null);
								setEmailLinkState("idle");
							}}
							placeholder="you@email.com"
							className="flex-1 bg-[#111] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-mgt-green"
						/>
						<button
							disabled={
								isSyncing ||
								emailLinkState === "checking" ||
								!EMAIL_REGEX.test(emailValue.trim())
							}
							onClick={async () => {
								if (!linkEmailWithPreCheck) return;
								setEmailFieldError(null);
								setEmailLinkState("checking");
								const result = await linkEmailWithPreCheck(emailValue);
								if (result === "duplicate") {
									// Merge modal will open; close this mini-form.
									setShowEmailInput(false);
									setEmailValue("");
									setEmailLinkState("idle");
								} else if (result === "ok") {
									// Privy modal is opening — close mini-form and let the user
									// complete the OTP flow there.
									setShowEmailInput(false);
									setEmailValue("");
									setEmailLinkState("idle");
								} else {
									setEmailLinkState("error");
									setEmailFieldError(
										"Could not check that email. Please try again."
									);
								}
							}}
							className="bg-mgt-green hover:bg-mgt-green/90 disabled:opacity-40 text-black text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
						>
							{emailLinkState === "checking" ? "Checking…" : "Continue"}
						</button>
						<button
							onClick={() => {
								setShowEmailInput(false);
								setEmailValue("");
								setEmailFieldError(null);
								setEmailLinkState("idle");
							}}
							disabled={emailLinkState === "checking"}
							className="text-xs text-gray-400 hover:text-gray-200 px-2 py-2 disabled:opacity-40"
						>
							Cancel
						</button>
					</div>
					{emailFieldError && (
						<p className="text-red-400 text-xs">{emailFieldError}</p>
					)}
				</div>
			)}

			{/* Phone pre-check mini-form — surfaces duplicate accounts before
			    opening Privy's modal. Mirrors the email pre-check (Task 10). */}
			{!hasType("phone") && linkPhoneWithPreCheck && showPhoneInput && (
				<div className="bg-[#231F20] border border-white/10 rounded-lg p-3 space-y-2">
					<p className="text-xs text-gray-300">
						Enter the phone number you want to link. We&apos;ll check if it
						already belongs to another account before sending a code.
					</p>
					<div className="flex gap-2">
						<input
							type="tel"
							autoFocus
							value={phoneValue}
							onChange={(e) => {
								setPhoneValue(e.target.value);
								setPhoneFieldError(null);
								setPhoneLinkState("idle");
							}}
							placeholder="+1 555 123 4567"
							className="flex-1 bg-[#111] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-mgt-green"
						/>
						<button
							disabled={
								isSyncing ||
								phoneLinkState === "checking" ||
								!PHONE_REGEX.test(phoneValue.trim())
							}
							onClick={async () => {
								if (!linkPhoneWithPreCheck) return;
								setPhoneFieldError(null);
								setPhoneLinkState("checking");
								const result = await linkPhoneWithPreCheck(phoneValue);
								if (result === "duplicate" || result === "ok") {
									setShowPhoneInput(false);
									setPhoneValue("");
									setPhoneLinkState("idle");
								} else {
									setPhoneLinkState("error");
									setPhoneFieldError(
										"Could not check that number. Please try again."
									);
								}
							}}
							className="bg-mgt-green hover:bg-mgt-green/90 disabled:opacity-40 text-black text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
						>
							{phoneLinkState === "checking" ? "Checking…" : "Continue"}
						</button>
						<button
							onClick={() => {
								setShowPhoneInput(false);
								setPhoneValue("");
								setPhoneFieldError(null);
								setPhoneLinkState("idle");
							}}
							disabled={phoneLinkState === "checking"}
							className="text-xs text-gray-400 hover:text-gray-200 px-2 py-2 disabled:opacity-40"
						>
							Cancel
						</button>
					</div>
					{phoneFieldError && (
						<p className="text-red-400 text-xs">{phoneFieldError}</p>
					)}
				</div>
			)}

			{/* Link buttons for missing account types */}
			<div className="flex flex-wrap gap-2 pt-1">
				{!hasType("email") && !linkEmailWithPreCheck && (
					<button
						onClick={linkEmail}
						disabled={isSyncing}
						className="text-xs px-3 py-1.5 rounded-lg bg-[#231F20] text-gray-300 hover:text-white hover:bg-[#2e2a2b] border border-white/10 disabled:opacity-40 transition-colors"
					>
						+ Link Email
					</button>
				)}
				{!hasType("email") && linkEmailWithPreCheck && !showEmailInput && (
					<button
						onClick={() => {
							setShowEmailInput(true);
							setEmailFieldError(null);
							setEmailLinkState("idle");
						}}
						disabled={isSyncing}
						className="text-xs px-3 py-1.5 rounded-lg bg-[#231F20] text-gray-300 hover:text-white hover:bg-[#2e2a2b] border border-white/10 disabled:opacity-40 transition-colors"
					>
						+ Link Email
					</button>
				)}
				{!hasType("phone") && !linkPhoneWithPreCheck && (
					<button
						onClick={linkPhone}
						disabled={isSyncing}
						className="text-xs px-3 py-1.5 rounded-lg bg-[#231F20] text-gray-300 hover:text-white hover:bg-[#2e2a2b] border border-white/10 disabled:opacity-40 transition-colors"
					>
						+ Link Phone
					</button>
				)}
				{!hasType("phone") && linkPhoneWithPreCheck && !showPhoneInput && (
					<button
						onClick={() => {
							setShowPhoneInput(true);
							setPhoneFieldError(null);
							setPhoneLinkState("idle");
						}}
						disabled={isSyncing}
						className="text-xs px-3 py-1.5 rounded-lg bg-[#231F20] text-gray-300 hover:text-white hover:bg-[#2e2a2b] border border-white/10 disabled:opacity-40 transition-colors"
					>
						+ Link Phone
					</button>
				)}
				{!hasType("twitter_oauth") && (
					<button
						onClick={linkTwitter}
						disabled={isSyncing}
						className="text-xs px-3 py-1.5 rounded-lg bg-[#231F20] text-sky-400 hover:text-sky-300 hover:bg-[#2e2a2b] border border-sky-400/20 disabled:opacity-40 transition-colors"
					>
						+ Link Twitter / X
					</button>
				)}
				{!hasType("google_oauth") && (
					<button
						onClick={linkGoogle}
						disabled={isSyncing}
						className="text-xs px-3 py-1.5 rounded-lg bg-[#231F20] text-blue-400 hover:text-blue-300 hover:bg-[#2e2a2b] border border-blue-400/20 disabled:opacity-40 transition-colors"
					>
						+ Link Google
					</button>
				)}
				{!hasType("discord_oauth") && (
					<button
						onClick={linkDiscord}
						disabled={isSyncing}
						className="text-xs px-3 py-1.5 rounded-lg bg-[#231F20] text-indigo-400 hover:text-indigo-300 hover:bg-[#2e2a2b] border border-indigo-400/20 disabled:opacity-40 transition-colors"
					>
						+ Link Discord
					</button>
				)}
				{!isMobile && hasPhantom && !hasExternalWallet && (
					<button
						onClick={linkPhantom}
						disabled={isSyncing}
						className="text-xs px-3 py-1.5 rounded-lg bg-[#231F20] text-purple-400 hover:text-purple-300 hover:bg-[#2e2a2b] border border-purple-400/20 disabled:opacity-40 transition-colors"
					>
						+ Link Phantom
					</button>
				)}
			</div>

			{isSyncing && (
				<p className="text-xs text-gray-500 flex items-center gap-1.5">
					<span className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
					Syncing...
				</p>
			)}
		</div>
	);
};
